/**
 * pdp-buy-box.js — Feel AWSM PDP buy box
 *
 * State machine: activePlan ('sub' | 'once') × activeBundle (1 | 3 | 6)
 *
 * ATC payloads (mirror live feelawsm.com):
 *   Subscribe:  POST {id, quantity: 1, selling_plan: <planId>}
 *   Once/1mo:   POST {id, quantity: 1}
 *   Once/3mo:   POST {id, quantity: 3}
 *   Once/6mo:   POST {id, quantity: 6}
 *
 * On success dispatches 'feel:cart-updated' so the cart drawer can update.
 */

(function () {
  'use strict';

  function initBuyBox() {
    const bb = document.querySelector('[data-buybox]');
    if (!bb) return;

    const variantId  = bb.dataset.variantId;
    const subPlanId  = bb.dataset.subPlanId || '0';

    /* Endpoint: prefer window.routes (Dawn global, market-aware) then data attr.
       Always ensure the .js suffix for JSON response (not HTML redirect). */
    const rawCartUrl = (window.routes && window.routes.cart_add_url)
      || bb.dataset.cartAddUrl
      || '/cart/add';
    const cartAddUrl = rawCartUrl.endsWith('.js') ? rawCartUrl : rawCartUrl + '.js';

    /* Liquid-formatted price strings (no JS money formatting needed) */
    const prices = {
      sub: bb.dataset.priceSub,
      1:   bb.dataset.price1,
      3:   bb.dataset.price3,
      6:   bb.dataset.price6,
    };

    let activePlan   = 'sub';
    let activeBundle = 1;

    const planEls      = bb.querySelectorAll('[data-plan]');
    const planToggles  = bb.querySelectorAll('[data-plan-toggle]');
    const bundleBtns   = bb.querySelectorAll('[data-bundle]');
    const oncePriceEl  = bb.querySelector('[data-once-price]');
    const atcPriceEl   = bb.querySelector('[data-atc-price]');
    const atcBtn       = bb.querySelector('[data-bb-atc]');

    /* ── Helpers ──────────────────────────────────────────────────── */

    function updatePriceDisplay() {
      const price = activePlan === 'sub' ? prices.sub : prices[activeBundle];
      if (atcPriceEl)   atcPriceEl.innerHTML  = price;
      if (oncePriceEl)  oncePriceEl.innerHTML  = prices[activeBundle];
    }

    function selectPlan(plan) {
      activePlan = plan;

      planEls.forEach(function (el) {
        el.classList.toggle('bb-plan--selected', el.dataset.plan === plan);
      });

      planToggles.forEach(function (btn) {
        btn.setAttribute('aria-expanded', btn.dataset.planToggle === plan ? 'true' : 'false');
      });

      updatePriceDisplay();
    }

    function selectBundle(bundle) {
      activeBundle = parseInt(bundle, 10);

      bundleBtns.forEach(function (btn) {
        btn.setAttribute('aria-pressed', String(parseInt(btn.dataset.bundle, 10) === activeBundle));
      });

      if (activePlan === 'once') updatePriceDisplay();
    }

    /* ── Event listeners ──────────────────────────────────────────── */

    planToggles.forEach(function (btn) {
      btn.addEventListener('click', function () {
        selectPlan(btn.dataset.planToggle);
      });
    });

    bundleBtns.forEach(function (btn) {
      btn.addEventListener('click', function () {
        selectBundle(btn.dataset.bundle);
      });
    });

    if (atcBtn) {
      atcBtn.addEventListener('click', function () {
        const payload = {
          id:       parseInt(variantId, 10),
          quantity: activePlan === 'sub' ? 1 : activeBundle,
        };

        if (activePlan === 'sub' && subPlanId && subPlanId !== '0') {
          payload.selling_plan = parseInt(subPlanId, 10);
        }

        atcBtn.classList.add('bb-atc--loading');
        atcBtn.disabled = true;

        fetch(cartAddUrl, {
          method:  'POST',
          headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
          body:    JSON.stringify(payload),
        })
          .then(function (r) { return r.json(); })
          .then(function (data) {
            if (data.status && data.status >= 400) {
              atcBtn.classList.add('bb-atc--error');
              setTimeout(function () { atcBtn.classList.remove('bb-atc--error'); }, 2000);
            } else {
              atcBtn.classList.add('bb-atc--success');
              setTimeout(function () { atcBtn.classList.remove('bb-atc--success'); }, 1500);
              /* Notify cart drawer: refresh contents + open */
              document.dispatchEvent(new CustomEvent('feel:cart-updated', { detail: data, bubbles: true }));
            }
          })
          .catch(function () {
            atcBtn.classList.add('bb-atc--error');
            setTimeout(function () { atcBtn.classList.remove('bb-atc--error'); }, 2000);
          })
          .finally(function () {
            atcBtn.classList.remove('bb-atc--loading');
            atcBtn.disabled = false;
          });
      });
    }

    /* ── Cross-sell ATC ──────────────────────────────────────────── */
    document.querySelectorAll('[data-bb-cs-add]').forEach(function (csBtn) {
      csBtn.addEventListener('click', function () {
        var card      = csBtn.closest('[data-variant-id]');
        var variantId = card ? card.dataset.variantId : null;
        if (!variantId) return;

        csBtn.disabled = true;

        fetch(cartAddUrl, {
          method:  'POST',
          headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
          body:    JSON.stringify({ id: parseInt(variantId, 10), quantity: 1 })
        })
          .then(function (r) { return r.json(); })
          .then(function () {
            csBtn.dataset.added = 'true';
            csBtn.textContent   = csBtn.getAttribute('data-added-label') || '✓ Added';
            document.dispatchEvent(new CustomEvent('feel:cart-updated'));
            setTimeout(function () {
              delete csBtn.dataset.added;
              csBtn.textContent = csBtn.getAttribute('data-label-original') || '+';
              csBtn.disabled    = false;
            }, 2500);
          })
          .catch(function () {
            csBtn.disabled = false;
          });
      });
      /* Cache original label for reset */
      csBtn.setAttribute('data-label-original', csBtn.textContent.trim());
    });

    /* ── Init state ───────────────────────────────────────────────── */
    updatePriceDisplay();
    selectBundle(activeBundle); /* reflect pre-selected tier in DOM on load */
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initBuyBox);
  } else {
    initBuyBox();
  }

  /* ── UGC Modal ────────────────────────────────────────────────────────────
   * Handles the UGC video carousel lightbox (feel-bb-ugc.liquid).
   *
   * Architecture:
   *  - [data-ugc-container] wraps carousel + modal HTML
   *  - [data-ugc-card] buttons: data-video-url present → has video
   *  - [data-ugc-modal] element moved to <body> for proper stacking context
   *  - Video src set lazily on open, cleared on close (no preload)
   *  - Prev/next navigate only among cards that have data-video-url
   *  - Prev/next hidden when ≤ 1 video card
   *  - ESC + overlay click closes modal
   *  - Focus trap inside modal for a11y
   */
  function initUGCModal(container) {
    if (container.dataset.ugcInit) return;
    container.dataset.ugcInit = 'true';

    var modal    = container.querySelector('[data-ugc-modal]');
    if (!modal) return;

    /* Move modal to <body> so it isn't clipped by overflow:hidden ancestors */
    document.body.appendChild(modal);

    var videoEl   = modal.querySelector('.bb-ugc-modal-video');
    var closeEls  = modal.querySelectorAll('[data-ugc-close]');
    var prevBtn   = modal.querySelector('[data-ugc-prev]');
    var nextBtn   = modal.querySelector('[data-ugc-next]');

    /* All cards with a video URL — used for prev/next navigation */
    var videoCards = Array.from(
      container.querySelectorAll('[data-ugc-card][data-video-url]')
    );
    var currentIdx = 0;

    /* Show/hide prev+next based on count */
    function updateNav() {
      var multi = videoCards.length > 1;
      if (prevBtn) prevBtn.hidden = !multi;
      if (nextBtn) nextBtn.hidden = !multi;
    }

    function loadVideo(url) {
      videoEl.src = url;
      videoEl.play().catch(function () { /* autoplay blocked — user taps play */ });
    }

    function openModal(idx) {
      currentIdx = idx;
      updateNav();
      loadVideo(videoCards[currentIdx].dataset.videoUrl);
      modal.hidden = false;
      document.body.style.overflow = 'hidden';
      /* Focus close button */
      var closeBtn = modal.querySelector('[data-ugc-close]');
      if (closeBtn) closeBtn.focus();
      document.addEventListener('keydown', onKey);
    }

    function closeModal() {
      modal.hidden = true;
      document.body.style.overflow = '';
      videoEl.pause();
      videoEl.removeAttribute('src');
      videoEl.load(); /* abort pending load */
      document.removeEventListener('keydown', onKey);
    }

    function goPrev() {
      currentIdx = (currentIdx - 1 + videoCards.length) % videoCards.length;
      videoEl.removeAttribute('src');
      loadVideo(videoCards[currentIdx].dataset.videoUrl);
    }

    function goNext() {
      currentIdx = (currentIdx + 1) % videoCards.length;
      videoEl.removeAttribute('src');
      loadVideo(videoCards[currentIdx].dataset.videoUrl);
    }

    /* Keyboard: ESC closes, ←→ navigate, Tab traps focus */
    function onKey(e) {
      if (e.key === 'Escape') { closeModal(); return; }
      if (e.key === 'ArrowLeft'  && videoCards.length > 1) { e.preventDefault(); goPrev(); return; }
      if (e.key === 'ArrowRight' && videoCards.length > 1) { e.preventDefault(); goNext(); return; }
      if (e.key === 'Tab') { trapFocus(e); }
    }

    function trapFocus(e) {
      var focusable = Array.from(
        modal.querySelectorAll('button:not([hidden]), [tabindex]:not([tabindex="-1"])')
      );
      if (focusable.length === 0) return;
      var first = focusable[0];
      var last  = focusable[focusable.length - 1];
      if (e.shiftKey) {
        if (document.activeElement === first) { e.preventDefault(); last.focus(); }
      } else {
        if (document.activeElement === last)  { e.preventDefault(); first.focus(); }
      }
    }

    /* Card click — only for cards with video */
    container.querySelectorAll('[data-ugc-card][data-video-url]').forEach(function (card) {
      card.addEventListener('click', function () {
        var idx = videoCards.indexOf(card);
        if (idx > -1) openModal(idx);
      });
    });

    /* Close triggers */
    closeEls.forEach(function (el) {
      el.addEventListener('click', closeModal);
    });

    if (prevBtn) prevBtn.addEventListener('click', goPrev);
    if (nextBtn) nextBtn.addEventListener('click', goNext);
  }

  function initAllUGC() {
    document.querySelectorAll('[data-ugc-container]').forEach(initUGCModal);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAllUGC);
  } else {
    initAllUGC();
  }
  document.addEventListener('shopify:section:load', function () {
    document.querySelectorAll('[data-ugc-container]:not([data-ugc-init])').forEach(initUGCModal);
  });

})();
